import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlusCircle } from "@fortawesome/pro-regular-svg-icons/faPlusCircle"
import { faTrashAlt } from "@fortawesome/pro-regular-svg-icons/faTrashAlt"
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons/faInfoCircle"
import { schoolYears, grades, savedSearches, lotteries, lotteryConfigurations, modals, fields } from '../../../ducks';
import duckify, { filterDuck } from '../../../utils/duckify';
import ModalComponent from '../../ModalComponent';
import { Button, Row } from 'react-bootstrap';
import ValidationErrors from '../../forms/ValidationErrors';
import autoAcceptOptionsConfig from '../../../../../config/auto_accept_options.json';

const RowWithLabel = ({ label, children }) => (
  <Row className='mb-3'>
    <label className="form-label col-form-label col-8 text-end fw-bold">{label}</label>
    <div className="controls col-16">
      {children}
    </div>
  </Row>
);

const LotteryConfigurationModal = (({ allDucksReady, configuration, grades, isLotteryPartitioned, lottery_option_1, onSubmit, savedSearches, schoolYears }) => {
  const isEditing = !!configuration;
  const hasLotteryResults = configuration && configuration.has_lottery_results;
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const lotteryOptionLabel = allDucksReady && lottery_option_1[0] ? lottery_option_1[0].display_name : 'Lottery Option 1'
  const autoAcceptOptions = autoAcceptOptionsConfig.autoAcceptOptions;
  const [formData, setFormData] = useState({
    apply_year_id: '',
    lottery_type: 'TieredLottery',
    auto_accept: 0,
    waitlist_siblings: false,
    lottery_option_1: '',
    grade_configurations: [],
    lottery_tiers: [{ weight: '', search_id: '' }]
  });

  useEffect(() => {
    if (configuration) {
      const gradeConfigs = (configuration.grade_configurations || []).map(grade => ({
        grade_id: grade.grade_id,
        grade_name: grade.grade_name,
        accept_limit: grade.accept_limit || 0
      }));

      setFormData({
        id: configuration.id,
        apply_year_id: configuration.apply_year_id || '',
        lottery_type: configuration.lottery_type || 'TieredLottery',
        auto_accept: configuration.auto_accept || 0,
        waitlist_siblings: configuration.waitlist_siblings || false,
        lottery_option_1: configuration.lottery_option_1 || '',
        grade_configurations: gradeConfigs,
        lottery_tiers: configuration.lottery_tiers && configuration.lottery_tiers.length > 0
          ? configuration.lottery_tiers.map(tier => ({
            weight: tier.weight || '',
            search_id: tier.search_id || ''
          }))
          : [{ weight: '', search_id: '' }]
      });
    }
  }, [configuration]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    setErrors(prev => {
      if (prev && prev[field]) {
        const { [field]: removedError, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  };

  const handleGradeSelectionChange = (selectedGrades) => {
    const selectAllOption = selectedGrades?.find(grade => grade.id === 'select-all')
    let gradesToProcess = selectedGrades || []

    // Add all active grades if 'select-all' was selected
    if (selectAllOption) {
      gradesToProcess = grades.filter(grade => grade.active)
    }
    const newGradeConfigurations = gradesToProcess
      .filter(grade => grade.id !== 'select-all')
      .map(grade => {
        // Preserve existing slot data if grade was previously selected
        const existingConfig = formData.grade_configurations.find(gc =>
          gc.grade_id === grade.id || gc.id === grade.id
        );
        return {
          grade_id: grade.id,
          grade_name: grade.name,
          accept_limit: existingConfig?.accept_limit || 0
        };
      });

    setFormData(prev => ({
      ...prev,
      grade_configurations: newGradeConfigurations
    }));

    setErrors(prev => {
      if (prev && prev.grade_configurations) {
        const { grade_configurations: removedError, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  };

  const handleGradeToggle = (gradeId, gradeName) => {
    const existingIndex = formData.grade_configurations.findIndex(gc => gc.grade_id === gradeId);

    if (existingIndex >= 0) {
      setFormData(prev => ({
        ...prev,
        grade_configurations: prev.grade_configurations.filter((_, index) => index !== existingIndex)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        grade_configurations: [
          ...prev.grade_configurations,
          { grade_id: gradeId, grade_name: gradeName, accept_limit: 0 }
        ]
      }));
    }
  };

  const handleGradeAcceptLimitChange = (gradeId, gradeName, accept_limit) => {
    setFormData(prev => ({
      ...prev,
      grade_configurations: prev.grade_configurations.map(gc =>
        gc.grade_id === gradeId ? { ...gc, accept_limit: parseInt(accept_limit) || 0 } : gc
      )
    }));

    setErrors(prev => {
      if (prev && prev[`grade_${gradeName}_accept_limit`]) {
        const { [`grade_${gradeName}_accept_limit`]: removedError, ...rest } = prev
        return rest
      }
      return prev
    })
  };

  const handleTierChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      lottery_tiers: prev.lottery_tiers.map((tier, i) =>
        i === index ? { ...tier, [field]: value } : tier
      )
    }));

    setErrors(prev => {
      if (prev && prev[`lottery_tiers_${index + 1}_${field}`]) {
        const { [`lottery_tiers_${index + 1}_${field}`]: removedError, ...rest } = prev
        return rest
      }
      return prev
    })
  };

  const addTier = () => {
    setFormData(prev => ({
      ...prev,
      lottery_tiers: [...prev.lottery_tiers, { weight: '', search_id: '' }]
    }));
  };

  const removeTier = (index) => {
    if (formData.lottery_tiers.length > 1) {
      setFormData(prev => ({
        ...prev,
        lottery_tiers: prev.lottery_tiers.filter((_, i) => i !== index)
      }));
    }

    // Clear all tier-related errors when any tier is removed
    setErrors(prev => {
      const newErrors = { ...prev };
      Object.keys(newErrors).forEach(key => {
        if (key.startsWith('lottery_tiers_')) {
          delete newErrors[key];
        }
      });
      return newErrors;
    });
  };

  const handleSubmit = () => {
    setIsSaving(true);
    onSubmit(formData, { setErrors, setIsSaving, isEditing });
  };

  return (
    <ModalComponent
      className="lottery-configuration-modal"
      title={isEditing ? 'Edit Lottery Configuration' : 'Create Lottery Configuration'}
      buttons={[
        <Button
          key="save"
          variant="primary"
          onClick={handleSubmit}
          disabled={isSaving || hasLotteryResults}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      ]}>

      <ValidationErrors errors={errors} />

      {hasLotteryResults && (
        <div className="alert alert-warning mb-3">
          <strong>Notice:</strong> This lottery configuration cannot be edited because the production lottery has already been run.
        </div>
      )}

      {/* Basic Configuration */}
      <RowWithLabel label={<>Apply Year <span style={{ color: 'red' }}>*</span></>}>
        <select
          className={`form-select ${errors.apply_year_id ? 'is-invalid' : ''}`}
          value={formData.apply_year_id}
          onChange={(e) => handleInputChange('apply_year_id', e.target.value)}
          id='apply_year_id'
        >
          <option value="">Select apply year</option>
          {schoolYears.map(year => (
            <option key={year.id} value={year.id}>{year.name}</option>
          ))}
        </select>
        {errors.apply_year_id && <div className="text-danger small mb-2">{errors.apply_year_id}</div>}
      </RowWithLabel>

      {/* Grade Selection */}
      <RowWithLabel label={<>Apply Grades <span style={{ color: 'red' }}>*</span></>}>
        <div>
          <Select
            multi
            closeOnSelect={false}
            removeSelected={false}
            placeholder="Select grades..."
            options={[
              { id: 'select-all', name: 'Select All Grades' },
              ...grades.filter(grade => grade.active)
            ]}
            labelKey="name"
            valueKey="id"
            value={formData.grade_configurations.map(gc => ({
              id: gc.grade_id,
              name: gc.grade_name
            }))}
            onChange={handleGradeSelectionChange}
            optionRenderer={(option) => {
              if(option.id === 'select-all'){
                return (
                  <div style={{ display: 'flex', alignItems: 'center', fontWeight: 'bold', borderBottom: '1px solid #eee', paddingBottom: '4px', marginBottom: '4px' }}>
                    <span>📋 {option.name}</span>
                  </div>
                )
              }

              const isSelected = formData.grade_configurations.some(gc => gc.grade_id === option.id);
              return (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => { }} // Handled by Select component
                    style={{ marginRight: '8px' }}
                    readOnly
                  />
                  <span>{option.name}</span>
                </div>
              );
            }}
            className={errors.grade_configurations ? 'is-invalid' : ''}
          />
          {errors.grade_configurations && (
            <div className="text-danger small mb-2">{errors.grade_configurations}</div>
          )}
        </div>
      </RowWithLabel>

      {/* Lottery Option 1 - only show if customer has lottery partitioning enabled */}
      {isLotteryPartitioned && lottery_option_1 && (
        <RowWithLabel label={<>{lotteryOptionLabel} <span style={{ color: 'red' }}>*</span></>}>
          <select
            className={`form-select ${errors.lottery_option_1 ? 'is-invalid' : ''}`}
            value={formData.lottery_option_1}
            onChange={(e) => handleInputChange('lottery_option_1', e.target.value)}
          >
            <option value="">Select lottery option</option>
            {lottery_option_1[0]?.options?.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
          {errors.lottery_option_1 && <div className="text-danger small mb-2">{errors.lottery_option_1}</div>}
        </RowWithLabel>
      )}

      {/* Size for each grade - only show when grades are selected */}
      {formData.grade_configurations.length > 0 && (
        <>
          <RowWithLabel label="Size for each grade">
            <div></div>
          </RowWithLabel>
          {formData.grade_configurations
            .sort((a, b) => {
              // Sort by the original order from the grades array
              const indexA = grades.findIndex(g => g.id === a.grade_id);
              const indexB = grades.findIndex(g => g.id === b.grade_id);
              return indexA - indexB;
            })
            .map(gradeConfig => {
              const gradeError = errors[`grade_${gradeConfig.grade_name}_accept_limit`];
              return (
                <RowWithLabel key={gradeConfig.grade_id} label={`${gradeConfig.grade_name}:`}>
                  <div className="d-flex align-items-center">
                    <div>
                      <input
                        type="number"
                        className={`form-control me-2 ${gradeError ? 'is-invalid' : ''}`}
                        style={{ width: '120px' }}
                        min="0"
                        value={gradeConfig.accept_limit === 0 ? '' : gradeConfig.accept_limit}
                        onChange={(e) => handleGradeAcceptLimitChange(gradeConfig.grade_id, gradeConfig.grade_name, e.target.value)}
                        placeholder="0"
                        onFocus={(e) => e.target.select()}
                        name='accept_limit'
                      />
                      {gradeError && (
                        <div className="text-danger small">{gradeError}</div>
                      )}
                    </div>
                    <button
                      type="button"
                      className="btn btn-link text-danger btn-sm p-0"
                      onClick={() => handleGradeToggle(gradeConfig.grade_id, gradeConfig.grade_name)}
                      title={`Remove ${gradeConfig.grade_name}`}
                    >
                      <FontAwesomeIcon
                        icon={faTrashAlt}
                        style={{ paddingLeft: '10px' }}
                      />
                    </button>
                  </div>
                </RowWithLabel>
              );
            })}
        </>
      )}

      {/* Show info when multiple grades are selected */}
      {formData.grade_configurations.length > 1 && (
        <Row className='mb-3'>
          <div className="col-24">
            <div className="alert alert-info mb-0" style={{ fontSize: '0.875rem', padding: '0.5rem' }}>
              <strong>
                <FontAwesomeIcon icon={faInfoCircle} style={{ paddingLeft: '15px', paddingRight: '15px' }} />
              </strong>
              Lotteries will be run starting with lowest grade first.
            </div>
          </div>
        </Row>
      )}

      {/* Lottery Options */}
      <RowWithLabel label="Automatically accept siblings?">
        <div>
          <select
            className="form-select"
            value={formData.auto_accept}
            onChange={(e) => handleInputChange('auto_accept', parseInt(e.target.value))}
          >
            {autoAcceptOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
      </RowWithLabel>

      {/* Only show waitlist siblings checkbox for relevant auto_accept options */}
      {(formData.auto_accept === 1 || formData.auto_accept === 2) && (
        <RowWithLabel label="Waitlist siblings together?">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={formData.waitlist_siblings}
              onChange={(e) => handleInputChange('waitlist_siblings', e.target.checked)}
            />
            <label className="form-check-label">
              Automatically waitlist same-grade siblings together?
            </label>
          </div>
        </RowWithLabel>
      )}

      {/* Lottery Type */}
      <RowWithLabel label="Lottery Type">
        <div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              name="lottery_type"
              value="TieredLottery"
              checked={formData.lottery_type === 'TieredLottery'}
              onChange={(e) => handleInputChange('lottery_type', e.target.value)}
            />
            <label className="form-check-label">Tiered Lottery</label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              name="lottery_type"
              value="WeightedLottery"
              checked={formData.lottery_type === 'WeightedLottery'}
              onChange={(e) => handleInputChange('lottery_type', e.target.value)}
            />
            <label className="form-check-label">Weighted Lottery</label>
          </div>
        </div>
      </RowWithLabel>

      {/* Lottery Tiers */}
      {formData.lottery_tiers.map((tier, index) => (
        <RowWithLabel key={index} label={<>{`Tier ${index + 1}`} {index === 0 ? <span style={{ color: 'red' }}>*</span> : ''}</>}>
          <div className="d-flex align-items-center">
            {/* Weight field - only show for WeightedLottery */}
            {formData.lottery_type === 'WeightedLottery' && (
              <input
                type="number"
                className={`form-control me-2 ${errors[`lottery_tiers_${index + 1}_weight`] ? 'is-invalid' : ''}`}
                style={{ width: '100px', marginBottom: '8px' }}
                placeholder="weight"
                value={tier.weight}
                onChange={(e) => handleTierChange(index, 'weight', e.target.value)}
              />
            )}

            <select
              className={`form-select me-2 ${errors[`lottery_tiers_${index + 1}_search_id`] ? 'is-invalid' : ''}`}
              value={tier.search_id}
              onChange={(e) => handleTierChange(index, 'search_id', e.target.value)}
            >
              <option value="">Select saved search</option>
              {savedSearches.map(search => (
                <option key={search.id} value={search.id}>{search.name}</option>
              ))}
            </select>

            {/* Remove button - only show for tiers after the first one */}
            {index > 0 && (
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={() => removeTier(index)}
                disabled={formData.lottery_tiers.length === 1}
              >
                Remove
              </button>
            )}
          </div>
          {errors[`lottery_tiers_${index + 1}_search_id`] && <div className="text-danger small mb-2">{errors[`lottery_tiers_${index + 1}_search_id`]}</div>}
          {errors[`lottery_tiers_${index + 1}_weight`] && <div className="text-danger small mb-2">{errors[`lottery_tiers_${index + 1}_weight`]}</div>}
        </RowWithLabel>
      ))}

      <RowWithLabel label="">
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={addTier}
        >
          <FontAwesomeIcon icon={faPlusCircle} /> Add Tier
        </button>
      </RowWithLabel>
    </ModalComponent>
  );
});

const mapDucksToProps = () => ({
  lottery_option_1: filterDuck(fields, { name: 'lottery_option_1' }),
  grades,
  savedSearches,
  schoolYears,
  lotteries,
})

const mapDispatchToProps = dispatch => ({
  onClose: () => dispatch(modals.actions.hide()),
  onSubmit: (lotteryConfiguration, { setErrors, setIsSaving, isEditing }) => {
    const action = isEditing
      ? lotteryConfigurations.actions.update(lotteryConfiguration)
      : lotteryConfigurations.actions.create(lotteryConfiguration);

    return dispatch(action)
      .then(() => dispatch(modals.actions.hide()))
      .then(() => dispatch(lotteryConfigurations.actions.markDirty()))
      .catch(err => {
        setIsSaving(false);
        const { response, ...cleanErrors } = err
        setErrors(cleanErrors)
      });
  }
})

export default duckify(mapDucksToProps, () => ({}), mapDispatchToProps)(LotteryConfigurationModal)
